// Fatia S1: empresas da conta (criar, arquivar, reativar) respeitando o limite do plano.
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Archive, Building2, Plus, RotateCcw } from 'lucide-react'
import type { CompanyDto } from '@impulso/shared'
import { api, errorMessage } from '../../lib/api'
import { useSession } from '../../lib/session'
import { useConfirm } from '../../ui/confirm'
import { useToast } from '../../ui/toast'
import { Empty, ErrorState, Loading, PageHead } from '../../ui'
import AccountGate from '../../features/account/AccountGate'
import UpgradeHint, { isPlanLimit } from '../../features/account/UpgradeHint'
import { useAccountCompanies } from '../../features/account/api'
import { dateBR } from '../../features/account/labels'
import { FormError } from '../../features/auth/AuthShell'

export default function Companies() {
  return <AccountGate permission="account.manage_companies">{(account) => <CompaniesInner accountId={account.id} kind={account.kind} canBill={account.permissions.includes('account.billing')} />}</AccountGate>
}

function CompaniesInner({ accountId, kind, canBill }: { accountId: string; kind: 'business' | 'agency'; canBill: boolean }) {
  const q = useAccountCompanies(accountId, 'all')
  const qc = useQueryClient()
  const { refresh } = useSession()
  const toast = useToast()
  const confirm = useConfirm()
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [segment, setSegment] = useState('')
  const [city, setCity] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const reload = async () => { await qc.invalidateQueries({ queryKey: ['account-companies', accountId] }); await refresh() }
  const atLimit = !!q.data && q.data.usage.companies >= q.data.maxCompanies
  const noun = kind === 'agency' ? 'cliente' : 'unidade'

  async function create(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const c = await api.post<CompanyDto>(`/accounts/${accountId}/companies`, { name, segment: segment || null, city: city || null })
      toast(`${c.name} criada.`)
      setName(''); setSegment(''); setCity(''); setShowForm(false)
      await reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  async function archive(id: string, nm: string) {
    if (!(await confirm({ title: `Arquivar ${nm}?`, message: 'A sincronização e o piloto param, e o histórico fica guardado por 90 dias.', confirmLabel: 'Arquivar', danger: true }))) return
    try { await api.post(`/companies/${id}/archive`); toast(`${nm} arquivada.`); await reload() } catch (err) { toast(errorMessage(err)) }
  }
  async function restore(id: string, nm: string) {
    try { await api.post(`/accounts/${accountId}/companies/${id}/restore`); toast(`${nm} reativada.`); await reload() } catch (err) { setError(err) }
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Empresas" sub={q.data ? `${q.data.usage.companies} de ${q.data.maxCompanies} ${kind === 'agency' ? 'empresas-clientes' : 'empresas'} ativas no seu plano` : undefined}>
        <button className="btn primary" onClick={() => setShowForm(!showForm)} disabled={!q.data}><Plus size={16} /> Nova empresa</button>
      </PageHead>

      {error != null && (isPlanLimit(error) ? <UpgradeHint accountId={accountId} message={errorMessage(error)} canBill={canBill} /> : <FormError error={errorMessage(error)} />)}

      {showForm && (atLimit ? (
        <UpgradeHint accountId={accountId} canBill={canBill} message={kind === 'agency' ? `Seu plano chegou ao limite de ${q.data!.maxCompanies} empresas. Mude de plano ou contrate empresas extras.` : `Seu plano permite ${q.data!.maxCompanies} empresa(s). O plano Profissional permite até 3 unidades.`} />
      ) : (
        <form className="card stack" style={{ gap: 14 }} onSubmit={create}>
          <h2>Nova {noun === 'cliente' ? 'empresa-cliente' : 'empresa'}</h2>
          <div className="grid g-3" style={{ gap: 12 }}>
            <div className="field"><label htmlFor="nc-name">Nome</label><input id="nc-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} /></div>
            <div className="field"><label htmlFor="nc-seg">Ramo</label><input id="nc-seg" className="input" value={segment} onChange={(e) => setSegment(e.target.value)} placeholder="Ex.: Pet shop" /></div>
            <div className="field"><label htmlFor="nc-city">Cidade</label><input id="nc-city" className="input" value={city} onChange={(e) => setCity(e.target.value)} /></div>
          </div>
          <div className="form-actions"><button type="button" className="btn ghost" onClick={() => setShowForm(false)}>Cancelar</button><button className="btn primary" disabled={busy}>{busy ? 'Criando…' : 'Criar'}</button></div>
        </form>
      ))}

      {q.isLoading && <Loading />}
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      {q.data && q.data.items.length === 0 && <Empty title="Nenhuma empresa ainda">Crie a primeira para conectar as contas de anúncio.</Empty>}
      {q.data && q.data.items.length > 0 && (
        <div className="card flush">
          <div className="table-wrap">
            <table className="table stack-sm">
              <thead><tr><th>Empresa</th><th>Cidade</th><th>Situação</th><th /></tr></thead>
              <tbody>
                {q.data.items.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="row" style={{ gap: 10 }}>
                        <span className="ic purple"><Building2 size={16} /></span>
                        <div>
                          {c.status === 'active' ? <Link className="strong" to={`/app/${c.id}/inicio`}>{c.name}</Link> : <span className="strong muted">{c.name}</span>}
                          <div className="small muted">{c.segment ?? 'Ramo não informado'}</div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Cidade">{c.city ?? '—'}</td>
                    <td data-label="Situação">
                      {c.status === 'archived' ? <span className="badge">Arquivada em {dateBR(c.archivedAt)}</span>
                        : c.onboardingCompletedAt ? <span className="badge green">Ativa</span>
                        : <Link to={`/app/${c.id}/onboarding`} className="badge amber">Configurar</Link>}
                    </td>
                    <td className="right" data-label="">
                      {c.status === 'active'
                        ? <button className="btn sm ghost" onClick={() => archive(c.id, c.name)}><Archive size={14} /> Arquivar</button>
                        : <button className="btn sm" onClick={() => restore(c.id, c.name)}><RotateCcw size={14} /> Reativar</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
